import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'

import type { FileRoutesByTo } from '@/routeTree.gen'

import { BookingError } from '@/components/booking/booking-error'
import { BookingStepper } from '@/components/booking/booking-stepper'
import { BlueHeader } from '@/components/layout/blue-header'
import { pushFunnelStep } from '@/lib/gtm'

const STEP_ROUTES = new Map<number, keyof FileRoutesByTo>([
  [1, '/recherche'],
  [2, '/informations'],
  [3, '/assurances'],
  [4, '/paiement'],
])

// Événement GTM/GA4 émis à l'entrée de chaque étape du tunnel, pour distinguer
// les étapes dans les rapports (au lieu d'un `page_view` générique unique).
const STEP_EVENTS = new Map<number, string>([
  [1, 'view_search_results'],
  [2, 'begin_checkout'],
  [3, 'view_insurance_step'],
  [4, 'add_payment_info'],
])

export const Route = createFileRoute('/_booking')({
  component: BookingLayout,
  errorComponent: BookingError,
})

export function BookingLayout() {
  const navigate = useNavigate()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const currentStep = getCurrentStep(currentPath)

  const sessionId = (routerState.location.search as { session?: string }).session

  // Signale l'étape courante du tunnel à GTM (no-op si GTM inactif).
  useEffect(() => {
    const event = STEP_EVENTS.get(currentStep)
    if (event) {
      pushFunnelStep(event, { checkout_step: currentStep })
    }
  }, [currentStep])

  // La restauration de scroll du routeur conserve la position entre les étapes du
  // tunnel (formulaires longs soumis depuis le bas de page) : chaque changement
  // d'étape repart du haut. `sessionId` fait partie des dépendances car changer
  // d'offre rejoue la même étape sous une autre session, sans changer le pathname.
  useEffect(() => {
    window.scrollTo({ behavior: 'instant', left: 0, top: 0 })
  }, [currentPath, sessionId])

  const handleStepClick = (step: number) => {
    if (step >= currentStep) return
    const route = STEP_ROUTES.get(step)
    if (!route) return

    if (step === 1) {
      void navigate({ to: route })
    } else if (sessionId) {
      void navigate({ search: { session: sessionId }, to: route })
    }
  }

  return (
    <div className="flex min-h-full w-full flex-col bg-[url('/images/default-background.jpg')] bg-cover bg-fixed bg-center bg-no-repeat">
      <BlueHeader />
      <div className="flex w-full flex-1 flex-col bg-white">
        <BookingStepper currentStep={currentStep} onStepClick={handleStepClick} />
        <div className="flex w-full flex-1 flex-col">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

// Étape du tunnel déduite du chemin courant, pour le stepper et les événements GTM.
function getCurrentStep(pathname: string): number {
  if (pathname.includes('/informations')) return 2
  if (pathname.includes('/assurances')) return 3
  if (pathname.includes('/paiement')) return 4
  return 1
}
